import Err from '@leverege/error'

import { Role, Roles, Permission, Permissions, Module, Modules, User } from './authz/index.js'
import { Resource, Resources } from './rsrc/index.js'
import System from './System.js'
import RouteUtils from './RouteUtils.js'
import PendingUser from './PendingUser.js'
import { getId } from '../Utils.js'
import Enum from './Enum.js'
import Enums from './Enums.js'
import ResourceEnum from './ResourceEnum.js'
import ResourceEnums from './ResourceEnums.js'
import Srv from './Srv.js'
import Service from './Service.js'
import Services from './Services.js'
import Rule from './Rule.js'
import Rules from './Rules.js'
import Blueprint from './Blueprint.js'
import ApiAccessKey from './ApiAccessKey.js'
import ApiAccessKeys from './ApiAccessKeys.js'
import UI from './UI.js'
import TemporaryUsers from './temporaryUser/TemporaryUsers.js'
import TemporaryUser from './temporaryUser/TemporaryUser.js'

export default function ProjectApi( api, project ) {
  
  let connect = null
  if ( project ) {
    if ( typeof project === 'string' ) {
      connect = api.projects().child( project )
    } else if ( project.id ) {
      connect = api.projects().child( project.id ) 
    } 
  }

  if ( connect == null ) {
    throw Err( 'invalid project' )
  }

  function getByUsername( name, options ) {
    return connect.child( `username/${name}` ).get( options )
  }

  const pendingUsers = PendingUser( connect )

  return {
    get : connect.get,
    update : connect.patch,
    delete : connect.delete,
    device : { 
      /**
       * For history and events.list see the Device/history and Device/events/list documentation
       * the only difference is the addition of the deviceId, systemId, and blueprintId root paths.
       */
      history( q, query ) { return connect.child( 'device/history' ).get( { ...query, q : JSON.stringify( q ) } ) },
      events() {
        return {
          list : ( q, query ) => connect.child( 'device/events' ).get( { ...query, q : JSON.stringify( q ) } ),
        }
      }
    },
    members( ) { return RouteUtils.users( connect, 'member' ) },

    user( id ) { return new User( connect, id, { allowMetadata : false } ) },
    users() {
      return RouteUtils.authzUsers( connect, 'user' )
    },

    accounts( ) {
      return {
        ...RouteUtils.collection( connect, 'account', true, true ),
        roles : ( id, opts ) => connect.child( 'account' ).child( getId( id ) ).child( 'role' ).get( opts )
      }
    },
    account( account ) {
      const cn = connect.child( 'account' ).child( getId( account ) )
      return {
        get : cn.get,
        update : cn.patch,
        delete : cn.delete,
        roles : cn.child( 'role' ).get
      }
    },

    oidcClients( ) { return RouteUtils.collection( connect, 'oidcClient', true, true ) },

    // rbac
    roles( ) { return new Roles( connect ) }, 
    role( id ) { return new Role( connect, id ) }, 
    permissions( ) { return new Permissions( connect ) }, 
    permission( id ) { return new Permission( connect, id ) }, 
    modules( ) { return new Modules( connect ) }, 
    module( id ) { return new Module( connect, id ) }, 

    pendingUsers( ) { return pendingUsers },
    enums() { return new Enums( connect ) },
    enum( type ) { return new Enum( connect, type ) },
    rules() { return new Rules( connect, true ) },
    rule( id ) { return new Rule( connect, id ) },
    resourceEnums() { return new ResourceEnums( connect ) },
    resourceEnum( type ) { return new ResourceEnum( connect, type ) },
    services() { return new Services( connect ) },
    service( type ) { return new Service( connect, type ) },
    srv( type ) { return new Srv( connect, type ) },
    networks( ) { return RouteUtils.collection( connect, 'network', true ) },
    scripts( ) { return RouteUtils.collection( connect, 'script', true, true ) },
    templates( ) { return RouteUtils.collection( connect, 'template', true, true ) },
    system( system ) { return System( connect, system, false ) },
    systems( ) { return RouteUtils.collection( connect, 'system' ) },
    messageRoutes( ) { return RouteUtils.collection( connect, 'messageRoute', true, true ) },
    scenarios( ) { return RouteUtils.collection( connect, 'scenario', true, true ) },
    timers( ) { return RouteUtils.collection( connect, 'timer', true, true ) },
    blueprint( id ) { return new Blueprint( api, { projectId : typeof project === 'string' ? project : project.id, id } ) },
    blueprints( ) { return RouteUtils.collection( connect, 'blueprint' ) },
    events( options ) { return RouteUtils.collection( connect, 'events' ).list( options ) },
    verify( options ) { return connect.child( 'verify' ).get( options, { useRefresh : true } ) },
    loginAs( userId, options ) { return connect.child( `loginAs/${userId}` ).get( options ) },
    getByUsername,
    
    resource( rsrc ) { return new Resource( connect, rsrc ) },
    resources() { return new Resources( connect ) },
    
    hooks( ) { return RouteUtils.collection( connect, 'hook' ) },
    hook( hookId ) {
      const cn = connect.child( 'hook' ).child( getId( hookId ) )
      return {
        get : cn.get,
        update : cn.patchRaw,
        delete : cn.delete
      }
    },
    twoFactorSettings() {
      const cn = connect.child( 'twoFactorSettings' )

      return {
        get : cn.get
      }
    },
    passwordlessLoginSettings() {
      const cn = connect.child( 'passwordlessLoginSettings' )

      return {
        get : cn.get
      }
    },
    passwordRequirements() {
      const cn = connect.child( 'passwordRequirements' )

      return {
        get : cn.get
      }
    },
    activeMaintenanceEvents() {
      return {
        list : options => connect.child( 'activeMaintenanceEvent' ).get( options )
      }
    },
    maintenanceEvents() {
      return RouteUtils.collection( connect, 'maintenanceEvent', false, true )
    },
    maintenanceEvent( eventId ) {
      const c = connect.child( 'maintenanceEvent' ).child( getId( eventId ) )
      return {
        get : c.get,
        delete : c.delete,
        update : c.patchRaw
      }
    },

    apiAccessKeys() { return new ApiAccessKeys( connect.child( 'apiAccess' ) ) },
    apiAccessKey( id ) { return new ApiAccessKey( connect.child( 'apiAccess' ), id ) },
    ui() { return new UI( connect.child( 'ui' ) ) },
    temporaryUsers() {
      return new TemporaryUsers( connect )
    },
    temporaryUser() {
      return new TemporaryUser( connect )
    },
    clientIds() {
      return RouteUtils.collection( connect, 'clientId', true, true )
    },
    clientId( id ) {
      const c = connect.child( 'clientId' ).child( getId( id ) )

      return {
        get : c.get,
        delete : c.delete,
        update : c.patchRaw
      }
    }
  }
}
